import type { ReactNode } from "react";
import type { Num, SeriesResponse } from "../types";
import { fmt } from "./format";
import { CANON } from "./settings";

/**
 * Раскрываемые подсказки «Как читать» к каждой панели графика рынка: что показано, как читать и что на панели
 * сейчас. Тексты — по книге Уильямса и ТЗ, простым языком; строка «Сейчас» строится из последних значений.
 */

function last(a: Num[] | undefined): Num {
  if (!a) return null;
  for (let i = a.length - 1; i >= 0; i--) if (a[i] != null) return a[i];
  return null;
}

function Help({ what, how, now }: { what: ReactNode; how: ReactNode[]; now?: ReactNode }) {
  return (
    <>
      <p>
        <b>Что показано.</b> {what}
      </p>
      <p className="pane-help-h">
        <b>Как читать</b>
      </p>
      <ul>
        {how.map((x, i) => (
          <li key={i}>{x}</li>
        ))}
      </ul>
      {now && (
        <p className="pane-help-now">
          <b>Сейчас.</b> {now}
        </p>
      )}
    </>
  );
}

const zone = (v: Num, hi = 80, lo = 20) =>
  v == null ? "нет данных" : v >= hi ? "верхняя (бычья) зона" : v <= lo ? "нижняя (медвежья) зона" : "середина, сигнала нет";

export function paneHelp(id: string, d: SeriesResponse, smartName: string, publicName: string): ReactNode {
  const s = d.summary;
  const smartGen = smartName === "Юрлица" ? "юрлиц" : "физлиц";
  const pubGen = publicName === "Юрлица" ? "юрлиц" : "физлиц";
  const p = d.params;

  switch (id) {
    case "price": {
      const sma = last(d.price.sma);
      const smaB = last(d.price.smaB);
      const below = s.close != null && sma != null && s.close < sma;
      return (
        <Help
          what={`цена фьючерса (непрерывный ряд из самого ликвидного контракта), средние, канал, линии входа, стоп и сигналы.`}
          how={[
            <>
              <b>Бар</b> — одна неделя (или день): вертикаль от минимума до максимума, засечка слева — открытие, справа —
              закрытие. Зелёный — закрылся выше открытия, красный — ниже.
            </>,
            <>
              <b>Серые пунктиры почти горизонтально</b> — средние цены за {p.smaWeeks} и {p.smaBWeeks} недель. Их не
              пробивают: они задают тренд. Цена ниже и средние идут вниз — берутся только продажи; выше и вверх — только
              покупки.
            </>,
            <>
              <b>Голубые ступеньки</b> — канал {p.channelWeeks} недель: максимум и минимум цены за прошлые полгода.
              <b> Розовая или зелёная вертикальная полоса</b> — неделя, когда цена закрылась за каналом (пробой вниз или
              вверх).
            </>,
            <>
              <b>Короткие наклонные отрезки</b> — линии тренда для входа. Красная проходит через два растущих минимума:
              её пробой вниз — вход в продажу. Зелёная — через два падающих максимума: пробой вверх — вход в покупку.
              Жирный пунктир с подписью «продажа ниже …» — линия, которую ждём сейчас; бледные — пробои, отброшенные
              фильтрами. Если линии нет, причина написана в строке «Линия входа» над графиком.
            </>,
            <>
              <b>Красная точечная лесенка</b> — стоп открытой сделки: подтягивается за ценой, касание — выход. Зелёная
              точечная — уровень перевхода после выбивания стопом.
            </>,
            <>
              <b>Треугольники</b>: ▲ зелёный — бычий сигнал, ▼ красный — медвежий. Закрашенный — по тренду (методика
              разрешает), полый — отфильтрован (против тренда или фильтра COT), только для сведения; такие по умолчанию скрыты и включаются галочкой над графиком. Наведите мышь — увидите название и дату.
            </>,
            <>
              <b>«балл N»</b> — общий сетап: сколько условий из 5 совпало (тренд, позиция {smartGen}, интерес, сезон,
              поведение толпы). Чем больше, тем сильнее.
            </>,
          ]}
          now={
            sma == null ? undefined : (
              <>
                закрытие {fmt.price(s.close)}, средняя {p.smaWeeks} н. — {fmt.price(sma)}
                {smaB != null && <>, {p.smaBWeeks} н. — {fmt.price(smaB)}</>}: цена {below ? "ниже" : "выше"} средней,{" "}
                {s.trend > 0 ? "тренд вверх — разрешены покупки" : s.trend < 0 ? "тренд вниз — разрешены продажи" : "тренд не определён"}.{" "}
                {s.position !== 0 ? `Открыта ${s.position > 0 ? "покупка" : "продажа"}, стоп ${fmt.price(s.stop)}.` : "Открытой сделки нет."}
              </>
            )
          }
        />
      );
    }
    case "trendbar":
      return (
        <Help
          what={`полоса состояния тренда для общего сетапа (средняя ${p.trendMode === "B" ? p.smaBWeeks : p.smaWeeks} недель).`}
          how={[
            "Зелёный участок — средняя растёт: общий сетап ищется только на покупку.",
            "Красный — средняя падает: только на продажу.",
            "Серый — средняя плоская: допустимы обе стороны.",
            "Пусто — данных для средней ещё мало.",
          ]}
          now={
            s.trendUnified === 1
              ? "покупки."
              : s.trendUnified === -1
                ? "продажи."
                : s.trendUnified === 2
                  ? "обе стороны (средняя плоская)."
                  : "не определён."
          }
        />
      );
    case "net":
      return (
        <Help
          what={`нетто-позиция ${smartGen}: все их длинные позиции минус короткие, в контрактах.`}
          how={[
            "Столбик выше нуля — группа в целом в покупках, ниже — в продажах. Важен не знак, а куда позиция сдвигается.",
            <>
              Пунктиры HH и LL — максимум и минимум нетто за {weeks(p.extremeWeeks)}. Нетто у верхнего пунктира — {smartGen}{" "}
              купили больше, чем когда-либо за этот срок (бычий знак); у нижнего — продали больше обычного (медвежий).
            </>,
            "Подпись «макс. за N л.» / «мин. за N л.» — рекорд за несколько лет: такие экстремумы у Уильямса самые сильные.",
            "Точечная линия — валовые лонги на своей шкале справа: видно, за счёт чего меняется нетто — покупок или закрытия шортов.",
          ]}
          now={
            <>
              нетто {fmt.compact(s.smartNet)} ({fmt.signed(s.smartNetChange)} за неделю)
              {s.recordYears ? `, ${s.recordYears > 0 ? "максимум" : "минимум"} за ${Math.abs(s.recordYears)} л.` : ""}.
            </>
          }
        />
      );
    case "cot":
      return (
        <Help
          what={`COT Index — где сейчас нетто-позиция ${smartGen} внутри своего диапазона: 0 — самая короткая позиция за окно, 100 — самая длинная.`}
          how={[
            <>
              Сплошная — окно {p.cotShortWeeks} недель (основная), пунктир — {weeks(p.cotLongWeeks)} («три года» по книге —
              долгий фон).
            </>,
            <>
              <b>80–100</b> (зелёная зона) — {smartGen} у максимума покупок, <b>0–20</b> (красная) — у максимума продаж.
            </>,
            <>
              <b>Зона для сетапа</b> (гл. 10 и 13): сплошная линия {100 - CANON.zone} и выше — ищем покупку,{" "}
              {CANON.zone} и ниже — продажу (или WILLCO в своей зоне), и тренд в ту же сторону. По умолчанию сделка
              открывается, когда полный сетап (тренд + COT/WILLCO + открытый интерес, балл 4–5) впервые сложился; в каноне
              — после пробоя линии тренда на цене.
            </>,
            "Сам по себе индекс — не вход, а условие: «большие деньги готовы». Вход — полный сетап (по умолчанию) или пробой линии тренда (канон).",
            `Оранжевый пунктир — индекс ${pubGen}: толпа почти всегда зеркальна, поэтому это контр-индикатор.`,
            "COT выше 50 для покупок в книге — проверка на истории (с. 121), а не правило входа: канон его не применяет.",
          ]}
          now={
            <>
              {fmt.idx(s.smartCotShort)} — {zone(s.smartCotShort)}; за {weeks(p.cotLongWeeks)} — {fmt.idx(s.smartCotLong)}.
            </>
          }
        />
      );
    case "willco":
      return (
        <Help
          what={`WILLCO — индекс Уильямса: доля нетто-позиции ${smartGen} в открытом интересе, разложенная по диапазону ${p.willcoWeeks} недель (0–100).`}
          how={[
            "В отличие от COT учитывает размер рынка: та же позиция при меньшем интересе весит больше.",
            "Выше 75 — сетап на покупку, полоса 75–80 — зона срабатывания; 90 и выше — сильный сетап.",
            "Ниже 25 — сетап на продажу.",
            "Если WILLCO и COT противоречат друг другу (один вверху, другой внизу), направление не определено — такой сетап слабый.",
          ]}
          now={<>{fmt.idx(s.smartWillco)} — {zone(s.smartWillco, 75, 25)}.</>}
        />
      );
    case "share":
      return (
        <Help
          what={`какая доля всего открытого интереса приходится на длинные (зелёная) и короткие (красная) позиции ${smartGen}, %.`}
          how={[
            <>
              Пунктиры — редкие уровни доли шорта в истории этого рынка: {p.csHighPct}-й перцентиль (выше бывает лишь в{" "}
              {100 - p.csHighPct}% недель) и {p.csLowPct}-й.
            </>,
            `Доля шорта выше верхнего пунктира — ${smartGen} продали необычно много: медвежий знак, особенно рядом с сезонной вершиной (заливка).`,
            `Пунктиры считаются по истории этого рынка. На фьючерсах на акции юрлица почти всегда в большом шорте (хеджируют купленные акции), поэтому 70 % шорта там может быть «мало». Доля шорта ниже нижнего пунктира значит только, что давление продаж слабее обычного, — это слабый бычий намёк, а не покупка ${smartGen}.`,
          ]}
          now={
            <>
              в лонгах {fmt.pct(s.smartShareLong)}, в шортах {fmt.pct(s.smartShareShort)}
              {shareVerdict(s.smartShareShort, last(d.groups[p.smartGroup].shortHigh), last(d.groups[p.smartGroup].shortLow))}
            </>
          }
        />
      );
    case "oiLine":
      return (
        <Help
          what={`открытый интерес — сколько всего контрактов открыто на рынке (лонги ${smartGen} + лонги ${pubGen}), и нетто ${smartGen} на своей шкале.`}
          how={[
            "Рост интереса — в рынок приходят новые деньги, падение — позиции закрывают.",
            "Само по себе направление интереса ничего не говорит: важно, кто его меняет (гл. 9).",
            "Цветные полосы — узнанные ситуации: зелёная — умные деньги покупают (дно, рост), красная — толпа покупает, а умные деньги продают (вершина). Наведите мышь — увидите номер и описание ситуации.",
          ]}
          now={<>открыто {fmt.compact(s.openInterest)} контрактов.</>}
        />
      );
    case "oi":
      return (
        <Help
          what={`индекс открытого интереса: где интерес сейчас в своём диапазоне за ${p.oiWeeks} недель (0–100).`}
          how={[
            "Выше 80 — рынок переполнен позициями (обычно толпой): по книге это чаще бывает у вершин.",
            "Ниже 20 — интерес иссяк, толпа ушла: чаще бывает у донышек.",
            "Наклонные отрезки — линии тренда на самом индексе: пробой вверх из нижней зоны — покупка, вниз из верхней — продажа.",
            "Для фьючерсов на индексы интерес скачет на квартальных экспирациях, поэтому там этот индекс в общий сетап не входит.",
          ]}
          now={<>{fmt.idx(s.oiStoch)} — {zone(s.oiStoch)}.</>}
        />
      );
    case "cotoi":
      return (
        <Help
          what={`два индекса на одной шкале: COT ${smartGen} (синяя линия) и индекс открытого интереса (оранжевая).`}
          how={[
            "Ищем расхождения за последние 4 недели.",
            `Красная подсветка — COT упал, а интерес вырос: рынок набирают покупками толпы, пока ${smartGen} продают. Так выглядит вершина.`,
            `Зелёная — COT вырос, а интерес упал: ${smartGen} покупают, пока толпа уходит. Так выглядит дно.`,
          ]}
        />
      );
    case "driver":
      return (
        <Help
          what={`кто меняет открытый интерес: серые столбики — изменение интереса за 4 недели, линии — сколько из него дали лонги ${smartGen} (синяя) и ${pubGen} (оранжевая).`}
          how={[
            `Интерес растёт за счёт оранжевой линии, а синяя падает — толпа покупает, ${smartGen} выходят: признак вершины.`,
            `Интерес падает, а синяя растёт — ${smartGen} покупают, пока толпа уходит: признак дна.`,
            "Обе линии рядом с нулём — ничего важного не происходит.",
          ]}
        />
      );
    case "progo":
      return (
        <Help
          what="ProGo (только дневной график): кто двигает цену — профессионалы или публика."
          how={[
            "Синяя — профессионалы: средний ход цены от открытия до закрытия дня за 14 дней. Внутри дня торгуют профессионалы.",
            "Оранжевая — публика: средний разрыв от вчерашнего закрытия до сегодняшнего открытия. Ночные приказы — это публика.",
            "Цена делает новый максимум, а синяя — нет: профессионалы не поддерживают рост (медвежье расхождение, оранжевый отрезок).",
            "Цена падает, а синяя растёт: профессионалы покупают на падении (бычье расхождение).",
          ]}
        />
      );
    case "progoAd":
      return (
        <Help
          what="те же данные ProGo в исходной форме автора: накопленные суммы ходов профессионалов и публики."
          how={[
            "Смотрят на наклон: линия профессионалов растёт — они покупают внутри дня, падает — продают.",
            "Расхождение наклона этой линии с ценой — то же, что дивергенции на панели ProGo.",
          ]}
        />
      );
    case "season": {
      const se = d.seasonal;
      return (
        <Help
          what={`сезонность — «средний год» цены: как в среднем менялась цена в каждую неделю года за ${se?.years ?? "все"} лет истории. Кривая повторяется на графике каждый год.`}
          how={[
            "Важен наклон, а не уровень: кривая идёт вверх — в эти недели цена обычно росла, вниз — обычно падала.",
            "Число в легенде — отклонение этой недели от среднего уровня «типичного года» (у акций — в долях: −0,09 ≈ на 9% ниже).",
            "Заливка — рядом сезонная вершина или дно. Сезон в сторону сделки добавляет балл общему сетапу.",
            "Чем меньше лет истории, тем слабее довод: при 2–3 годах один необычный год сильно искажает кривую.",
          ]}
          now={
            se ? (
              <>
                на ближайшие {se.horizonWeeks} нед. сезон обычно{" "}
                {se.bias > 0 ? "растущий" : se.bias < 0 ? "падающий" : "нейтральный"}; лет в расчёте — {se.years}.
              </>
            ) : undefined
          }
        />
      );
    }
    case "jiler":
      return (
        <Help
          what={`метод Джайлера: насколько нетто-позиция ${smartGen} (в % от открытого интереса) отличается от обычной для этого месяца по прошлым годам (до ${d.jiler.years} л.).`}
          how={[
            `Выше +5 — ${smartGen} держат больше лонгов, чем обычно в этом месяце: бычий знак.`,
            "Ниже −5 — больше шортов, чем обычно: медвежий знак.",
            "Между пунктирами ±5 — в пределах нормы, по книге игнорируется.",
            "Больше 40 по модулю — аномалия, книга советует осторожность.",
            "Нужно минимум два прошлых года с тем же месяцем, поэтому у молодых рынков линия начинается поздно.",
          ]}
          now={
            s.jilerDev == null ? undefined : (
              <>
                {s.jilerDev.toFixed(1)} —{" "}
                {Math.abs(s.jilerDev) < 5 ? "в пределах нормы" : s.jilerDev > 0 ? "бычье отклонение" : "медвежье отклонение"}.
              </>
            )
          }
        />
      );
    case "holders": {
      const hl = last(d.groups.FIZ?.holdersLong);
      const hs = last(d.groups.FIZ?.holdersShort);
      return (
        <Help
          what="сколько счетов физлиц держат длинные (зелёная) и короткие (красная) позиции. Это число людей, а не контрактов."
          how={[
            "Независимая мера участия толпы: показывает, сколько людей в рынке, даже если позиции маленькие.",
            "Резкий рост числа счетов в лонгах на росте цены — толпа набегает, часто ближе к вершине.",
            "Массовый уход из лонгов на падении — капитуляция толпы, часто ближе ко дну.",
            "Изменение (лонги − шорты) за 4 недели учитывается в балле общего сетапа как «поведение толпы».",
          ]}
          now={hl != null ? <>в лонгах {fmt.compact(hl)}, в шортах {fmt.compact(hs)}.</> : undefined}
        />
      );
    }
    default:
      return null;
  }
}

/**
 * Доля шорта против своих редких уровней (M10). Уровни — по истории ЭТОГО рынка: у фьючерсов на акции юрлица почти
 * всегда в большом шорте (хедж купленных акций), поэтому «мало шортов» здесь — мало относительно их обычного шорта,
 * а не мало вообще. Формулировка это говорит прямо, чтобы 72 % шорта не называлось «бычьим знаком» без оговорок.
 */
function shareVerdict(cs: Num, hi: Num, lo: Num) {
  if (cs == null || hi == null || lo == null) return ".";
  const range = `обычно доля их шорта на этом рынке от ${fmt.pct(lo)} до ${fmt.pct(hi)}`;
  // Отличие меньше полупункта на глаз не видно (71,5 % «ниже» 71,5 %) — это граница, а не сигнал.
  if (Math.abs(cs - lo) < 0.5 || Math.abs(cs - hi) < 0.5) return `. Это граница обычного диапазона (${range}) — сигнала нет.`;
  if (cs > hi) return `. Это больше, чем почти всегда (${range}): юрлица продают необычно много — медвежий знак.`;
  if (cs < lo)
    return `. Юрлица всё ещё в шорте, но меньше, чем почти всегда (${range}): давление продаж ослабло. Это слабый бычий намёк, а не покупка юрлиц.`;
  return `. Это обычный уровень (${range}) — сигнала нет.`;
}

function weeks(w: number) {
  if (w % 52 === 0) {
    const y = w / 52;
    return `${y} ${y === 1 ? "год" : y < 5 ? "года" : "лет"}`;
  }
  return `${w} недель`;
}
